import React, { useState, useMemo, useEffect } from 'react';
import {
    Autocomplete,
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    Button,
    Checkbox,
    Chip,
    createFilterOptions,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Typography,
    TypographyProps,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useNavigate } from 'react-router-dom';

interface MultiSelectAutocompleteProps {
    ids: string[];
    label?: string;
    onChange?: (newIDs: string[]) => void;
    placeholder?: string;
    addNewItemLabel?: string;
    dialogTitle?: string;
    error?: string;
    dataTestId?: string;
    getOptionDisabled?: (option: string) => boolean;
    getChipProps?: (option: string) => object;
    customValidateModalValue?: (value: string) => { error: boolean; helperText: string };
    labelVariant?: TypographyProps['variant'];
    showAddNewButton?: boolean;
    width?: string;
    options?: string[];
    redirectUrlOnAdd?: string;
    maxSelection?: number;
}

function areSetsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const val of a) {
        if (!b.has(val)) return false;
    }
    return true;
}

const alphaNumRegex = /^[A-Za-z0-9]{26}$/;
const numericalRegex = /^\d{6,8}$/;

const MultiSelectAutocomplete: React.FC<MultiSelectAutocompleteProps> = ({
    ids,
    label = 'IDs',
    onChange,
    placeholder = 'Select or add a new ID',
    addNewItemLabel = 'Add New ID',
    dialogTitle = 'Add A New ID',
    error,
    dataTestId,
    getOptionDisabled,
    getChipProps,
    customValidateModalValue,
    labelVariant,
    showAddNewButton = true,
    width = '300px',
    options,
    redirectUrlOnAdd,
    maxSelection
}) => {
    const filterOptions = createFilterOptions<string>({
        stringify: (option) => option,
        matchFrom: 'start',
    });

    const initIDs = useMemo(
        () => new Set(ids),
        [ids]
    );
    const initOptions = useMemo(() => (options ? new Set(options) : new Set(ids)), [options, ids]);
    const [idHistory, setIdHistory] = useState<Set<string>>(initOptions);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(initIDs);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalInputValue, setModalInputValue] = useState('');
    const [idError, setIdError] = useState(false);
    const [idHelperText, setIdHelperText] = useState('');

    const actualOptions = useMemo(() => {
        const opts = Array.from(idHistory);
        if (redirectUrlOnAdd && opts.length === 0) {
            opts.push('+Add Prize');
        }
        return opts;
    }, [idHistory, redirectUrlOnAdd]);
    const navigate = useNavigate();

    useEffect(() => {
        if (initIDs.size > 0 && !areSetsEqual(selectedIds, initIDs)) {
            setSelectedIds(initIDs);
            setIdHistory((prev) => new Set([...initIDs, ...prev]));
        }
    }, [initIDs, selectedIds]);

    useEffect(() => {
        if (options) {
            setIdHistory(new Set(options));
        }
    }, [options]);

    function updateIds(newSet: Set<string>) {
        if (maxSelection && newSet.size > maxSelection) {
            newSet = new Set(Array.from(newSet).slice(0, maxSelection));
        }
        setSelectedIds(newSet);
        onChange?.(Array.from(newSet));
    }

    function validateModalValue(value: string) {
        const trimmed = value.trim();

        if (!trimmed) {
            setIdError(true);
            setIdHelperText('Cannot add an empty ID');
            return;
        }

        if (!alphaNumRegex.test(trimmed)) {
            setIdError(true);
            setIdHelperText('This field must contain an alphanumeric string with exactly 26 characters.');
            return;
        }

        if (idHistory.has(trimmed)) {
            setIdError(true);
            setIdHelperText('This ID already exists');
            return;
        }

        setIdError(false);
        setIdHelperText('');
    }

    function defaultValidateModalValue(value: string) {
        const trimmed = value.trim();
        if (!trimmed) {
            return { error: true, helperText: 'Cannot add an empty ID' };
        }
        if (!(alphaNumRegex.test(trimmed) || numericalRegex.test(trimmed))) {
            return { error: true, helperText: 'This field must be alphanumeric characters of 26 or numeric of 6-8.' };
        }
        return { error: false, helperText: '' };
    }

    function resetModal() {
        setModalOpen(false);
        setIdError(false);
        setIdHelperText('');
        setModalInputValue('');
    }

    function handleAddModalId() {
        validateModalValue(modalInputValue);
        if (idError) return;

        const newSet = new Set([...selectedIds, modalInputValue.trim()]);
        setIdHistory((prev) => prev.add(modalInputValue.trim()));
        updateIds(newSet);
        resetModal();
    }

    function handleModalInput(
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
        ) {
            const { value } = event.target;
            setModalInputValue(value);

            // Use the custom validation if provided.
            const { error, helperText } = customValidateModalValue
            ? customValidateModalValue(value)
            : defaultValidateModalValue(value);

            setIdError(error);
            setIdHelperText(helperText);
    }

    function handleModalValidation(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
        handleModalInput(event);
    }

    function handleRemoveOption(idValues: string[], _idDetails: AutocompleteChangeDetails<string>) {
        updateIds(new Set(idValues));
    }

    function handleSelectOption(idValues: string[], idDetails: AutocompleteChangeDetails<string>) {

        if (!idDetails.option) return;

        if (redirectUrlOnAdd && idDetails.option === '+Add Prize') {
            navigate(redirectUrlOnAdd);
            return;
        }

        if (idDetails.option.startsWith(addNewItemLabel + ':')) {
            const match = idDetails.option.match(/"([^"]*)"/);
            if (match && match[1]) {
                setModalInputValue(match[1]);
                setModalOpen(true);
            }
        } else {
            updateIds(new Set(idValues));
        }
    }

    function handleIdsInput(
        _event: React.SyntheticEvent<Element, Event>,
        idValues: string[],
        actionType: AutocompleteChangeReason,
        idDetails: AutocompleteChangeDetails<string>
    ) {
        if (actionType === 'removeOption') {
            handleRemoveOption(idValues, idDetails);
        }
        if (actionType === 'selectOption') {
            handleSelectOption(idValues, idDetails);
        }
    }

    return (
        <Grid item>
            <Grid container direction="column" alignItems="flex-start" justifyContent="flex-start">
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ maxWidth: width }}
                >
                    <Grid item>
                        <Typography variant={labelVariant ?? 'body1'}>{label}</Typography>
                    </Grid>
                    {showAddNewButton && (
                        <Grid item>
                        <Button onClick={() => setModalOpen(true)} className='row-link' sx={{ textTransform: 'none' }} data-testid={`${dataTestId}-add-btn`}>
                            + {addNewItemLabel}
                        </Button>
                    </Grid>
                    )}
                </Grid>

                <Grid item>
                    <Autocomplete
                        multiple
                        clearOnBlur
                        selectOnFocus
                        openOnFocus
                        forcePopupIcon
                        disableClearable
                        handleHomeEndKeys
                        disableCloseOnSelect
                        id="reusable-id-autocomplete"
                        value={Array.from(selectedIds)}
                        options={actualOptions}
                        onChange={handleIdsInput}
                        sx={{ width: width }}
                        getOptionDisabled={getOptionDisabled}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox
                                    icon={<CheckBoxOutlineBlankIcon fontSize="medium" color="primary" />}
                                    checkedIcon={<CheckBoxIcon fontSize="medium" />}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {option}
                            </li>
                        )}
                        filterOptions={(options, params) => {
                            const filtered = filterOptions(options, params);
                            const inputVal = params.inputValue.trim();
                            if (showAddNewButton && inputVal !== '' && !filtered.includes(inputVal) && !idHistory.has(inputVal)) {
                                filtered.push(`${addNewItemLabel}: "${inputVal}"`);
                            }
                            return filtered;
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={selectedIds.size === 0 ? placeholder : ''}
                                error={Boolean(error)}
                                helperText={error}
                                inputProps={{
                                    ...params.inputProps,
                                    readOnly: !showAddNewButton,
                                }}
                            />
                        )}
                        renderTags={(idValue, getTagProps) =>
                            idValue.map((option, index) => (
                                <Chip
                                    key={option}
                                    label={option}
                                    {...getTagProps({ index })}
                                    {...(getChipProps ? getChipProps(option) : {})}
                                />
                            ))
                        }
                    />
                </Grid>

                <Dialog open={modalOpen} fullWidth>
                    <DialogTitle align="center">
                        + {dialogTitle}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            data-testid={`${dataTestId}-input`}
                            fullWidth
                            required
                            autoFocus
                            type="text"
                            margin="dense"
                            autoComplete="off"
                            variant="outlined"
                            error={idError}
                            value={modalInputValue}
                            onFocus={handleModalValidation}
                            onBlur={handleModalValidation}
                            onChange={handleModalInput}
                            helperText={idHelperText}
                            inputProps={{ maxLength: 250, minLength: 1 }}
                            aria-label={dialogTitle}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={resetModal} data-testid={`${dataTestId}-modal-btn-cancel`}>
                            Cancel
                        </Button>
                        <Button variant="contained" disabled={idError} onClick={handleAddModalId} data-testid={`${dataTestId}-modal-btn-add`}>
                            Add
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid>
        </Grid>
    );
};

export default MultiSelectAutocomplete;
